import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SignupShell } from '@/features/auth/components/SignupShell';
import { PasswordChecklist } from '@/features/auth/components/PasswordChecklist';
import { isPasswordValid } from '@/features/auth/constants/passwordRules';
import { useResetPasswordMutation } from '@/features/auth/api/useAuthMutations';
import { inputValidation } from '@/utils/function';

type ResetForm = {
  newPassword: string;
  confirmPassword: string;
};

type ResetLocationState = {
  email?: string;
  resetToken?: string;
};

const sanitizePasswordInput = (value: string) =>
  inputValidation(value, { emoji: true, specialCharacters: false });

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as ResetLocationState | null) ?? {};
  const resetPassword = useResetPasswordMutation();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetForm>({ mode: 'onChange' });

  const password = watch('newPassword') || '';
  const confirmPassword = watch('confirmPassword') || '';
  const passwordsMatch = Boolean(
    password && confirmPassword && password === confirmPassword,
  );
  const canSubmit =
    Boolean(state.email && state.resetToken) &&
    passwordsMatch &&
    isPasswordValid(password);

  useEffect(() => {
    if (!state.email || !state.resetToken) {
      navigate('/forgot-password', { replace: true });
    }
  }, [navigate, state.email, state.resetToken]);

  const onSubmit = (data: ResetForm) => {
    if (!state.email || !state.resetToken) return;
    resetPassword.mutate({
      email: state.email,
      resetToken: state.resetToken,
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword,
    });
  };

  const inputClass = (hasError?: boolean) =>
    `signup-input box-border h-[44px] w-full min-w-0 rounded-[9px] border bg-white px-3.5 pr-11 text-sm leading-none text-[#0F1817] outline-none transition-[border-color,box-shadow] placeholder:text-[#A9B4B1] ${
      hasError
        ? 'border-red-400'
        : 'border-[#E2E9E7] hover:border-[#C9D3D1]'
    }`;

  return (
    <SignupShell>
      <div className="signup-form-align flex w-full min-w-0 flex-col">
        <h1 className="mb-2 text-[22px] font-semibold tracking-tight text-[#0F1817]">
          Change password
        </h1>
        <p className="mb-6 text-sm leading-relaxed text-[#7C8B88]">
          Choose a strong new password for your account.
        </p>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full min-w-0 flex-col gap-1 text-left"
        >
          <div className="mb-3">
            <label className="mb-1.5 block text-[12.5px] font-medium text-[#3E524F]">
              New password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={inputClass(Boolean(errors.newPassword))}
                placeholder="Enter new password"
                {...register('newPassword', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Minimum 8 characters required',
                  },
                  setValueAs: sanitizePasswordInput,
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute top-1/2 right-2.5 -translate-y-1/2 border-none bg-transparent p-1 text-[#7C8B88] hover:text-[#0F1817]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.newPassword ? (
              <p className="mt-1 text-[11px] text-red-500">
                {errors.newPassword.message}
              </p>
            ) : null}
            {password.length > 0 ? (
              <PasswordChecklist password={password} />
            ) : null}
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-[12.5px] font-medium text-[#3E524F]">
              Confirm password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                className={inputClass(Boolean(errors.confirmPassword))}
                placeholder="Re-enter password"
                {...register('confirmPassword', {
                  required: 'Confirm password is required',
                  setValueAs: sanitizePasswordInput,
                  validate: (value) =>
                    value === password || 'Passwords do not match',
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute top-1/2 right-2.5 -translate-y-1/2 border-none bg-transparent p-1 text-[#7C8B88] hover:text-[#0F1817]"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirmPassword ? (
              <p className="mt-1 text-[11px] text-red-500">
                {errors.confirmPassword.message}
              </p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={!canSubmit || resetPassword.isPending}
            className="signup-btn flex h-[46px] w-full items-center justify-center rounded-[9px] border-none text-[14.5px] font-semibold text-white transition-all hover:enabled:-translate-y-px disabled:cursor-not-allowed disabled:opacity-45"
          >
            {resetPassword.isPending ? 'Updating…' : 'Change password'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="mt-5 border-none bg-transparent p-0 text-sm text-[#7C8B88] transition-colors hover:text-[#0F1817]"
        >
          ← Back
        </button>
      </div>
    </SignupShell>
  );
}
